import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
const _excluded = ["group"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { EditorState, Selection } from 'prosemirror-state';
import { EditorView } from 'prosemirror-view';
import { history } from 'prosemirror-history';
import { keymap } from 'prosemirror-keymap';
import _isEqual from 'lodash/isEqual';
import _debounce from 'lodash/debounce';
import cx from 'classnames';
import DOMPurify from 'dompurify';
import './Editor.scss';
import { processPlugins } from './plugins';
import { buildMenuItems, groupCustomBlocks } from './utils/menu';
import { buildKeymap } from './utils/keymap';
import menuPlugin from './plugins/Menu';
import AttachmentList, { PreviewModal } from '../Attachments';
import EditorEvents from './EditorEvents';
import { uploadDroppedFiles } from './plugins/Media/actions';
import isEqual from 'lodash/isEqual';
import { handleTodoClick } from './plugins/Todos/actions';
import { MENU_GROUP_TYPES } from './utils/constants';
import { convertToHtmlFromSchema, getDocFromHtml } from './utils';
export const DEFAULT_MARKS = ['textAlignment', 'underline', 'strikeThrough', 'textColor', 'textHighlight'];
class Editor extends PureComponent {
  constructor(props) {
    var _this;
    super(props);
    _this = this;
    /**
     * @description Listens the editor changes and converts editor's document into JSON, HTML and plaintext formats
     * @param {Object} docContent - Document object of the editor
     */
    this.onChange = _debounce(docContent => {
      const {
        onChange,
        hasInlineStyles
      } = this.props;
      const jsonContent = docContent.toJSON();
      const sanitizedHtml = convertToHtmlFromSchema(this.schema, jsonContent);
      const sanitizedDoc = getDocFromHtml(this.schema, sanitizedHtml);
      const sanitizedJson = sanitizedDoc.toJSON().content;
      const finalSanitizedHtml = convertToHtmlFromSchema(this.schema, sanitizedJson);
      const div = document.createElement('div');
      div.setAttribute('style', 'opacity: 0; position: absolute; z-index: -1');
      if (hasInlineStyles) {
        const htmlWrapDiv = document.createElement('div');
        htmlWrapDiv.setAttribute('style', 'font-size: 14px;font-family: "Proxima-Nova-Regular", Arial, Helvetica, sans-serif;');
        htmlWrapDiv.innerHTML = finalSanitizedHtml;
        div.append(htmlWrapDiv);
      } else {
        div.innerHTML = finalSanitizedHtml;
      }
      document.body.appendChild(div);
      const editorChanges = {
        json: sanitizedJson,
        text: DOMPurify.sanitize(div.innerText.replace(/(\n)+/g, ' ')),
        html: DOMPurify.sanitize(div.innerHTML)
      };
      this.setState({
        editorChanges
      });
      div.remove();
      if (onChange) {
        const {
          files
        } = this.state;
        onChange(editorChanges, files);
      }
    }, 500);
    this.onClosePreview = () => {
      this.setState({
        showPreview: false,
        previewInfo: {}
      });
    };
    this.onClickAttachment = _ref => {
      let {
        url,
        name,
        thumbUrl
      } = _ref;
      this.setState({
        showPreview: true,
        previewInfo: {
          url,
          name,
          thumbUrl
        }
      });
    };
    this.onAddAttachment = file => {
      const {
        files
      } = this.state;
      const newFiles = [...files, file];
      this.setState({
        files: newFiles
      });
      const {
        onChangeFiles
      } = this.props;
      if (onChangeFiles) {
        onChangeFiles(newFiles);
      }
    };
    this.onPreviewImage = (src, alt, title) => {
      if (!src) return;
      this.setState({
        showPreview: true,
        previewInfo: {
          url: src,
          name: title || alt
        }
      });
    };
    this.onPreviewMedia = function (url) {
      let name = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '';
      if (!url) return;
      _this.setState({
        showPreview: true,
        previewInfo: {
          url,
          name
        }
      });
    };
    this.handleDrop = e => {
      const {
        pluginOptions = {}
      } = this.props;
      const options = _objectSpread(_objectSpread({}, {
        inline: true
      }), pluginOptions.media);
      options.events = this.events;
      options.editorId = this.editorId;
      uploadDroppedFiles(this.view, e, options);
    };
    /**
     * @description Replaces the editor's content with given input
     * @param {Array|string} content - Raw input to be converted & shown in the editor (Either JSON string or array)
     * @param {Object} parseOptions - Custom parsing rules
     */
    this.setContent = function () {
      let content = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
      let parseOptions = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {
        preserveWhitespace: true
      };
      const doc = _this.createDocument(content, parseOptions);
      const {
        state
      } = _this.view;
      if (isEqual(doc, state.doc)) return;
      const newState = EditorState.create({
        schema: state.schema,
        doc,
        plugins: state.plugins
      });
      const selection = Selection.atEnd(newState.tr.doc);
      const tr = newState.tr.setSelection(selection);
      _this.view.updateState(newState.apply(tr));
    };
    this.handleOnBlur = () => {
      const {
        isReadOnly,
        onBlur
      } = this.props;
      if (!isReadOnly && onBlur && this.view && !this.view.hasFocus()) {
        const {
          editorChanges,
          files
        } = this.state;
        onBlur(editorChanges, files);
        this.setState({
          editorChanges: null
        });
      }
    };
    this.getPluginsAndSchema = () => {
      const {
        plugins: activePlugins,
        pluginOptions,
        customKeymap,
        renderAsHtml,
        marks,
        showUndoRedoMenuItem
      } = this.props;
      const defaultPlugins = ['placeholder', 'media', 'link'];
      const {
        schema,
        plugins,
        menuItems
      } = processPlugins([...activePlugins, ...defaultPlugins], pluginOptions, this.editorId, this.events, marks, this.props);
      const finalMenuItems = [...buildMenuItems(this.schema || schema, {
        showUndoRedoMenuItem
      })];
      const customGroup = _objectSpread(_objectSpread({}, groupCustomBlocks(this.schema || schema)), {}, {
        items: []
      });
      menuItems.forEach(_ref2 => {
        let {
            group
          } = _ref2,
          menuItem = _objectWithoutProperties(_ref2, _excluded);
        if (group && group !== MENU_GROUP_TYPES.CUSTOM) {
          const groupObj = finalMenuItems.find(item => item.id === group);
          if (groupObj) {
            groupObj.items.push(menuItem);
            return;
          }
        }
        customGroup.items.push(menuItem);
      });
      if (customGroup.items.length > 0) {
        finalMenuItems.push(customGroup);
      }
      return {
        schema,
        plugins: [history(), keymap(_objectSpread(_objectSpread({}, buildKeymap(this.schema || schema)), customKeymap)), ...plugins, ...(renderAsHtml ? [] : [menuPlugin(this.editorId, finalMenuItems)])]
      };
    };
    this.editorRef = /*#__PURE__*/React.createRef();
    this.schema = null;
    this.view = null;
    this.emptyDocument = {
      type: 'doc',
      content: [{
        type: 'paragraph'
      }]
    };
    const {
      files: _files
    } = props;
    this.state = {
      editorChanges: null,
      showPreview: false,
      previewInfo: {},
      files: _files || []
    };
    this.editorId = Math.random().toString(36).substring(7);
  }
  static getDerivedStateFromProps(props, state) {
    const {
      files
    } = props;
    const {
      prevFiles
    } = state;
    if (!_isEqual(files, prevFiles)) {
      return {
        files: [...files],
        prevFiles: [...files]
      };
    }
    return null;
  }
  componentDidMount() {
    const {
      enableAutoFocus
    } = this.props;
    this.initEvents();
    this.initEditor();
    if (enableAutoFocus) {
      this.autoFocus();
    }
  }
  componentDidUpdate(prevProps) {
    const {
      defaultValue,
      resetContent,
      renderAsHtml,
      pluginOptions
    } = this.props;
    const {
      defaultValue: prevDefaultValue,
      resetContent: prevResetContent,
      pluginOptions: prevPluginOptions,
      renderAsHtml: prevRendeAsHtml
    } = prevProps;
    if (!renderAsHtml) {
      if (prevRendeAsHtml) {
        if (this.view) {
          this.view.destroy();
          this.view = null;
          this.schema = null;
        }
        this.initEditor();
      }
      if (resetContent !== prevResetContent && resetContent) {
        this.setContent('');
        this.setState({
          files: [],
          editorChanges: null
        });
      } else if (!_isEqual(prevDefaultValue, defaultValue)) {
        this.setContent(defaultValue);
      }
      if (!_isEqual(pluginOptions, prevPluginOptions)) {
        const {
          state
        } = this.view;
        const {
          plugins
        } = this.getPluginsAndSchema();
        const newState = state.reconfigure({
          plugins
        });
        this.view.updateState(newState);
        this.view.update(this.getEditorProps(this.schema, newState));
        this.view.focus();
      }
    } else if (!prevRendeAsHtml && this.view) {
      this.view.destroy();
      this.initEditor();
    }
    if (renderAsHtml && !_isEqual(prevDefaultValue, defaultValue)) {
      this.setContent(defaultValue);
    }
  }
  componentWillUnmount() {
    if (process.env.NODE_ENV === 'test') {
      global.tapEditors = global.tapEditors.filter(_ref3 => {
        let {
          id
        } = _ref3;
        return id !== this.editorId;
      });
      delete global.tapEditorViewIdMap[this.editorId];
    }
  }
  autoFocus() {
    this.view.focus();
  }
  initEvents() {
    this.events = new EditorEvents(this.editorId);
    this.events.on('addAttachment', this.onAddAttachment);
    this.events.on('previewImage', this.onPreviewImage);
    this.events.on('previewMedia', this.onPreviewMedia);
  }

  /**
   * @description Configures and initilizes the editor based on the enabled plugins
   */
  initEditor() {
    const {
      defaultValue
    } = this.props;
    const {
      schema,
      plugins
    } = this.getPluginsAndSchema();
    this.schema = schema;
    const doc = defaultValue ? this.createDocument(defaultValue) : this.schema.nodeFromJSON(this.emptyDocument);
    const editorState = EditorState.create({
      doc,
      plugins
    });
    this.view = new EditorView(this.editorRef.current, this.getEditorProps(schema, editorState));
    if (process.env.NODE_ENV === 'test') {
      if (!global.tapEditors) {
        global.tapEditors = [];
      }
      if (!global.tapEditorViewIdMap) {
        global.tapEditorViewIdMap = {};
      }
      global.tapEditors.push({
        id: this.editorId,
        view: this.view
      });
      global.tapEditorViewIdMap[this.editorId] = this.view;
    }
  }
  getEditorProps(schema, state) {
    const {
      renderAsHtml,
      isReadOnly,
      onClickNode,
      debounceBlur
    } = this.props;
    return {
      state,
      editable: () => !isReadOnly && !renderAsHtml,
      dispatchTransaction: transaction => {
        const newState = this.view.state.apply(transaction);
        this.view.updateState(newState);
        if (!isReadOnly && !transaction.before.eq(transaction.doc)) {
          this.onChange(newState.doc.content);
        }
      },
      handleDOMEvents: {
        blur: () => {
          debounceBlur ? setTimeout(this.handleOnBlur, 700) : this.handleOnBlur();
        }
      },
      handleClickOn: (view, pos, node, nodePos, event) => {
        if (event.target.className === 'todo-checkbox') {
          handleTodoClick(view, node, nodePos);
        }
        if (node && node.type) {
          if ((isReadOnly || renderAsHtml) && (node.type === schema.nodes.image || node.type === schema.nodes.media)) {
            const {
              url,
              name,
              src,
              alt,
              title
            } = node.attrs;
            this.setState({
              showPreview: true,
              previewInfo: {
                url: url || src,
                name: name || title || alt
              }
            });
          }
          if (onClickNode) {
            onClickNode(node);
          }
        }
        return false;
      }
    };
  }

  /**
   * @description Converts raw input into editor's document object
   * @param {Array|string} data - Raw input (Either JSON string or array)
   * @param {Object} parseOptions - Custom parsing rules
   */
  createDocument(data) {
    let parseOptions = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    let content = data;
    if (content === null) {
      return this.schema.nodeFromJSON(this.emptyDocument);
    }
    let contentType = typeof content;
    if (typeof content === 'string') {
      try {
        content = JSON.parse(content);
        contentType = 'object';
      } catch (e) {
        content = data;
      }
    }
    if (contentType === 'object') {
      content = convertToHtmlFromSchema(this.schema, content);
      contentType = 'string';
    }
    if (contentType === 'string') {
      return getDocFromHtml(this.schema, content, parseOptions);
    }
    return this.schema.nodeFromJSON(this.emptyDocument);
  }
  render() {
    const {
      noBorder,
      renderAsHtml,
      maxFileCount,
      canDeleteFile,
      onFilePreview,
      onDeleteFile,
      hasInlineStyles,
      showThumbnail,
      downloadAttachmentWithPresignedUrl
    } = this.props;
    const {
      showPreview,
      previewInfo,
      files
    } = this.state;
    return /*#__PURE__*/React.createElement("div", {
      className: "tap-editor-wrap",
      "data-id": this.editorId
    }, /*#__PURE__*/React.createElement("div", {
      className: cx("tap-editor".concat(hasInlineStyles ? '' : ' no-inline'), {
        noborder: noBorder,
        viewonly: renderAsHtml
      }),
      onDrop: this.handleDrop
    }, /*#__PURE__*/React.createElement("div", {
      key: "editor",
      className: "tap-editor-content",
      ref: this.editorRef
    }), files.length > 0 && /*#__PURE__*/React.createElement(AttachmentList, {
      showThumbnail: showThumbnail,
      files: files,
      maxCount: maxFileCount,
      canDelete: canDeleteFile,
      onClick: onFilePreview || this.onClickAttachment,
      onDeleteFile: onDeleteFile,
      downloadAttachmentWithPresignedUrl: downloadAttachmentWithPresignedUrl
    }), showPreview && /*#__PURE__*/React.createElement(PreviewModal, {
      name: previewInfo.name,
      url: previewInfo.thumbUrl || previewInfo.url,
      type: previewInfo.type,
      onClose: this.onClosePreview
    })));
  }
}
Editor.propTypes = {
  allowUploadDataUrl: PropTypes.bool,
  canDeleteFile: PropTypes.bool,
  customKeymap: PropTypes.object,
  debounceBlur: PropTypes.bool,
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.bool]),
  embedPatterns: PropTypes.array,
  enableAutoFocus: PropTypes.bool,
  files: PropTypes.array,
  hasInlineStyles: PropTypes.bool,
  isReadOnly: PropTypes.bool,
  marks: PropTypes.array,
  maxFileCount: PropTypes.number,
  noBorder: PropTypes.bool,
  pluginOptions: PropTypes.object,
  plugins: PropTypes.arrayOf(PropTypes.string),
  renderAsHtml: PropTypes.bool,
  resetContent: PropTypes.bool,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onChangeFiles: PropTypes.func,
  onClickNode: PropTypes.func,
  onDeleteFile: PropTypes.func,
  onFilePreview: PropTypes.func,
  showUndoRedoMenuItem: PropTypes.bool,
  showThumbnail: PropTypes.bool
};
Editor.defaultProps = {
  allowUploadDataUrl: false,
  canDeleteFile: false,
  customKeymap: {},
  debounceBlur: true,
  defaultValue: '',
  embedPatterns: [],
  enableAutoFocus: false,
  files: [],
  hasInlineStyles: false,
  isReadOnly: false,
  marks: DEFAULT_MARKS,
  maxFileCount: 3,
  noBorder: false,
  pluginOptions: {},
  plugins: [],
  renderAsHtml: false,
  resetContent: false,
  onBlur: null,
  onChange: null,
  onChangeFiles: null,
  onClickNode: null,
  onDeleteFile: null,
  onFilePreview: null,
  showUndoRedoMenuItem: false,
  showThumbnail: true
};
export default Editor;